/**
 * XMLHttpRequest wrapper to send request to server
 *
 * @author Michel Heitbrink
 */

import EncryptionGPG from './encryption/gpg/Encryption';
import EncryptionRSA from './encryption/rsa-aes/Encryption';

const Encryption = ( ENCRYPTION.gpg ) ? EncryptionGPG : EncryptionRSA;

import Plugin from './core/Plugin';
import Helpers from './core/Helpers';

class ServerRequest extends Plugin {

	static pluginName = "ServerRequest";

	static getDefaults() {

		return {
			baseurl: window.location.href,
			peanuts: true,
			useMethod:  true,
			secure: false,
			status: 'body',
			headers: {}
		};
	}

	static getName() {
		return ServerRequest.pluginName;
	}

	constructor( options ) {
		super(options);

		this.encryptiontype = ( ENCRYPTION.gpg ) ? 'gpg' : 'rsa-aes';
	}

	/**
	 * Performs a GET request to the server.
	 * @param  {String} target
	 * @param  {Object} data
	 * @param  {Object} options
	 * @return {Promise}
	 */
	get( target, data, options, headers ) {

		let method = null;

		if ( this.options.peanuts && this.options.useMethod ) {
			method = target;
		}
		else {
			options = ( options ) ? options : {};
			options.url = target;
		}

		return this.call( 'GET', method, data, options, null, headers );
	}

	/**
	 * Performs a GET request to the server to read a custom url without a method
	 * @param  {String} method
	 * @param  {Object} data
	 * @param  {Object} options
	 * @return {Promise}
	 */
	read( url ){
		return this.call( 'GET', null, null, { url: url } );
	}

	/**
	 * Performs a POST request to the server.
	 * @param  {String} target
	 * @param  {Object} data
	 * @param  {Object} options
	 * @return {Promise}
	 */
	post( target, data, options, files, headers ) {

		let method = null;

		if ( this.options.peanuts && this.options.useMethod ) {
			method = target;
		}
		else {
			options = ( options ) ? options : {};
			options.url = target;
		}

		return this.call( 'POST', method, data, options, files, headers );
	}

	/**
	 * Performs a PUT request to the server.
	 * @param  {String} target
	 * @param  {Object} data
	 * @param  {Object} options
	 * @return {Promise}
	 */
	put( target, data, options, files, headers ) {

		let method = null;

		if ( this.options.peanuts && this.options.useMethod ) {
			method = target;
		}
		else {
			options = ( options ) ? options : {};
			options.url = target;
		}

		return this.call( 'PUT', method, data, options, files, headers );
	}

	/**
	 * Performs a PUT request to the server.
	 * @param  {String} target
	 * @param  {Object} data
	 * @param  {Object} options
	 * @return {Promise}
	 */
	delete( target, data, options, files, headers ) {

		let method = null;

		if ( this.options.peanuts && this.options.useMethod ) {
			method = target;
		}
		else {
			options = ( options ) ? options : {};
			options.url = target;
		}

		return this.call( 'DELETE', method, data, options, files, headers );
	}

	/**
	 * Performs a XHR request to the server. Maybe converting to websocket in the future.
	 * @param  {String} type    POST | GET | DELETE | PUT
	 * @param  {String} method
	 * @param  {Object} data
	 * @param  {Object} options
	 * @param  {Object} files
	 * @return {Promise}
	 */
	call( type, method, data, options, files, headers ) {

		return new Promise( ( resolve, reject ) => {

			var requestData = {};

			if ( options == undefined || options == null ) {
				options = {};
			}

			let encrypt = ( options && typeof options.encrypt == 'boolean' ) ? options.encrypt :  this.options.secure;
			let prefix = ( options && typeof options.prefix == 'boolean' ) ? options.prefix :  this.options.peanuts;

			/**
			 * Uppercase the type
			 */
			type = type.toUpperCase();

			/**
			 * Check if data is set and add it to the formData. Also adding the method to the formdata.
			 */
			if ( method ) {
				requestData.method = method;
			}

			/**
			 * Add binary files to object to post to server. Sending files only permitted with POST
			 */
			if ( files && prefix && type !== 'GET' ) {

				for ( var fileKey in files ) {
					requestData[fileKey] = files[fileKey];
				}
			}
			else if ( method == null && typeof data == 'string' ) {
				requestData = data;
			}

			/**
			 * merge headers with default headers
			 * @type {[type]}
			 */
			headers = headers ? { ...this.options.headers, ...headers } : this.options.headers;

			/**
			 * Send data encrypted or not
			 */
			if ( data && encrypt ) {

				const encryption =  new Encryption();

				/**
				 * Default request header for encrypted data
				 */
				const encryptedHeaders =  { ...headers,
					'Content-Encoding': 'base64/utf-8',
					'Content-Encryption': ( this.encryptiontype == 'rsa-aes' ) ? 'rsa/aes' : 'gpg'
				};

				/**
				 * Adding package to the params parameter
				 */
				if ( typeof data == 'object' ) {
					requestData.params = data;
				}
				else if ( method == null && typeof data == 'string' ) {
					requestData = data;
				}

				/**
				 * Format the package as a json string.
				 * Only used with objects
				 */
				if ( typeof requestData == 'object' ) {

					requestData = JSON.stringify(requestData);
					encryptedHeaders['Content-Type'] = 'application/json';

				}

				/**
				 * Encrypt and send the data
				 */
				encryption.encrypt( requestData ).then( ( encrypted ) => {

					this.send(type, encrypted, options, encryptedHeaders).then(resolve).catch( ({status, result, message}) => {

						if ( status == 426 && typeof encryption.resetKeys == "function" ) {

							encryption.resetKeys();

							encryption.encrypt( requestData ).then( ( encrypted ) => {

								this.send(type, encrypted, options, encryptedHeaders).then(resolve).catch(reject);

							}).catch(reject);

						} else { reject({status, result, message}); }

					});

				}).catch( (e) => {} );
			}
			else {

				if ( data && typeof data == 'object' && prefix ) {

					/**
					 * Adding keys,values to the param object.
					 * Problematically are the names in with brackets. Like in a form.
					 * So we concat them into the params var.
					 */
					for ( var dataKey in data ) {

						let postkey =  ( dataKey.indexOf("[") >= 0 ) ? dataKey.replace('[', '][') : dataKey + ']';

						if ( Array.isArray( data[dataKey]) ){

							let arrayKey = ( postkey.slice(-2) == '[]' ) ? postkey.slice(0, -2) : postkey;

							data[dataKey].forEach( (value, index) => {
								requestData['params[' + arrayKey + '[' + index + ']'] = (typeof value == "object") ? JSON.stringify(value) : value;
							});

						}
						else if ( data[dataKey] !== null && typeof data[dataKey] == 'object' ){

							let objectKeys = this.toKeyValue(data[dataKey], {}, '');
							postkey = ( postkey.slice(-1) == ']' ) ? postkey : postkey + ']';

							Object.keys(objectKeys).forEach( (k) => {
								requestData['params[' + postkey + k ] = objectKeys[k];
							});
						}
						else {
							postkey = ( postkey.slice(-1) == ']' ) ? postkey : postkey + ']';
							requestData['params[' + postkey ] = data[dataKey];
						}
					}
				}
				else if ( prefix == false ) {
					requestData = data;
				}

				/**
				 * Adding encoding to the data.
				 */
				if ( options.encode )  {

					requestData = this.encodeData(options.encode, requestData);

					if ( headers['Content-Encoding'] == undefined ) {
						headers = Object.assign(headers, this.getEncodeHeader(options.encode));
					}
				}

				/**
				 * send package
				 */
				this.send( type, requestData, options, headers ).then(resolve).catch(reject);
			}
		});
	}

	/**
	 * Send data to server
	 * @param  {String} type        POST | GET | DELETE | PUT
	 * @param  {Object} requestData
	 * @param  {Object} options
	 * @param  {Object} headers
	 * @return {Promise}
	 */
	send( type, requestData, options, headers ) {

		const self = this;

		return new Promise( ( resolve, reject ) => {

			var xhr = new XMLHttpRequest();

			/**
			 * Send data to current location.
			 */
			xhr.open( type, this.getUrl( type, requestData, options ), true );

			/**
			 * Let peanuts recognise it is a ajax call
			 */
			xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');

			var body = null;

			if ( headers['Content-type'] == undefined && type !== 'POST' && type !== 'GET' ) {
				headers['Content-type'] = 'application/x-www-form-urlencoded';
			}

			if ( headers['Content-type'] == 'application/x-www-form-urlencoded' ) {
				body = this.serialize(requestData);
			}
			else {
				body = ( requestData instanceof Object ) ? this.objectToFormData( requestData ) : requestData;
			}

			/**
			 * Add custom headers
			 */
			if ( headers ) {
				for ( var key in headers ) {
					xhr.setRequestHeader(key, headers[key]);
				}
			}

			/**
			 * Add callback when request is done. The callback triggers the resolve of the current promise.
			 */
			xhr.onload = function() {

				/**
				 * Formatting result based on the header Content-Type.
				 */
				self.formatResponse( xhr, this.responseText ).then( (response ) => {

					let location = ( options && options.status ) ? options.status : self.options.status;

					let result = ( location == 'header' ) ? response : response.result,
						status = ( location == 'header' ) ? this.status : response.status,
						message = ( location == 'header' ) ? undefined : response.msg;

					/**
					 * Trigger correct callback bases on HTTP status.
					 */
					if ( status >= 200 && status < 300 ){
						resolve( { result, status, message } );
					}
					else {
						reject( { result, status, message } );
					}

				}).catch(reject);
			};

			xhr.onerror = function(e) {
				reject({ message: e, status: 500});
			};

			xhr.send( body );
		});
	}

	/**
	 * Submit form based on selector
	 * @param  {String} selector
	 * @return {Promise}
	 */
	submit( form, method, params, options, headers ) {

		return new Promise( ( resolve, reject ) => {

			/**
			 * Get form with selector. The selector must be unique
			 * @type {[type]}
			 */
			if ( typeof form == 'string' ) {
				form = document.querySelector(form);
			}

			if ( form ) {

				/**
				 * Get all inputs
				 */
				const inputs = form.querySelectorAll('input:not([type=checkbox]):not([type=radio]), input[type=checkbox]:checked, input[type=radio]:checked, textarea');

				var data = {},
					files = {};

				for( var i = 0; i < inputs.length; i++ ){

					if ( inputs[i] && inputs[i].name ){

						if(inputs[i].type !== 'submit' ){
							/**
							 * Check if input is file input. if so add file to params
							 */
							if(inputs[i].type == 'file' ){

								if ( inputs[i].files.length > 0 ) {
									files[inputs[i].name] = inputs[i].files[0];
								}
							}

							/**
							 * Add value to params
							 */
							else {

								if ( typeof data[inputs[i].name] == 'string' ) {
									var firstValue = data[inputs[i].name];

									data[inputs[i].name] = [];
									data[inputs[i].name].push(firstValue);
								}

								if ( Array.isArray(data[inputs[i].name]) ) {
									data[inputs[i].name].push(inputs[i].value);
								}
								else {
									data[inputs[i].name] = inputs[i].value;
								}
							}
						}
					}
				}

				/**
				 * Check if select is single select or multiselect
				 */
				const selects = form.querySelectorAll('select');

				for( var i = 0; i < selects.length; i++ ){

					if ( selects[i] && selects[i].name ){

						if ( selects[i].multiple ) {

							data[selects[i].name] = [];
							/**
							 * Add value to a array for a multiple select
							 */
							for (var j = 0; j < selects[i].length; j++) {

								if ( selects[i].options[j].selected ) {
									data[selects[i].name].push(selects[i].options[j].value);
								}
							}
						}
						else {

							/**
							 * Add single value to data
							 */

							if ( typeof data[selects[i].name] == 'string' ) {
								var firstValue = data[selects[i].name];

								data[selects[i].name] = [];
								data[selects[i].name].push(firstValue);
							}

							if ( Array.isArray(data[selects[i].name]) ) {
								data[selects[i].name].push(selects[i].value);
							}
							else {
								data[selects[i].name] = selects[i].value;
							}
						}
					}
				}

				/**
				 * add custom params to the sending params
				 */
				if ( params ) {
					for( var key in params ) {
						data[key] = params[key];
					}
				}

				this.call( 'POST', method, data, options, files, headers).then(resolve).catch(reject);
			}
			else {
				reject({ message:'form not found' });
			}
		});
	}

	/**
	 * Formats result of a xhr request to correct format based on Content-type.
	 * @param  {XMLHttpRequest} xhr
	 * @param  {String} response
	 * @return {Object | String}
	 */
	formatResponse( xhr, response ) {

		return new Promise( (resolve, reject) => {

			/**
			 * If Content-Encoding is base64/utf-8 then the response is encoded with base64.
			 * So decode the response
			 */
			if ( xhr.getResponseHeader("Content-Encoding") === 'base64/utf-8' && Helpers.isBase64(response) ) {
				 response = atob(response);
			 }

			if ( xhr.getResponseHeader("Content-Encryption") === 'rsa/aes' ) {

				var encryption = new Encryption(this.options);

				encryption.decrypt( response, false ).then( ( decrypted ) => {

					resolve( this.formatContentType(xhr, decrypted) );

				}).catch(reject);
			}
			else {
				resolve(this.formatContentType(xhr, response));
			}
		});

	}

	/**
	 * Format received data based on the content type header
	 * @param  {XMLHttpRequest} xhr
	 * @param  {String} response
	 * @return {Object | String}
	 */
	formatContentType( xhr, response ) {

		switch ( xhr.getResponseHeader("Content-Type") ) {

			case 'application/json':
					return JSON.parse(response);
				break;

			default:
					return response;
				break;
		}
	}

	/**
	 * Encode data based on option `encode`
	 * @param  {String} type
	 * @param  {Object | String} data
	 * @return {Object | String}
	 */
	encodeData(type, data) {

		if ( data ) {

			if ( type == 'base64' ) {

				if ( Array.isArray(data) || typeof data == 'object' ) {
					data = JSON.stringify(data);
				}

				return btoa(data);
			}
		}

		return data;
	}

	/**
	 * Get the encoding header based on option `encode`
	 * @param  {String} type
	 * @return {Object}
	 */
	getEncodeHeader(type) {

		switch (type) {

			case 'base64':
					return {'Content-Encoding': 'base64/utf-8'};
				break;
		}

		return null;
	}

	/**
	 * Create URL for request
	 * If the type is GET the data will be formatted to a query string
	 * @param  {String} type
	 * @param  {FormData} formData
	 * @param  {Object} options
	 * @return {String}
	 */
	getUrl( type, data, options ) {

		var url = window.location.href,
			queryString = "";

		/**
		 * Overrule the default url.
		 * First check if the options contains a url. If not check if the global options base url is set.
		 * Override the default url with one of those options.
		 */
		if ( options.url ) {
			url = options.url;
		}
		else if ( this.options.baseurl ) {
			url = this.options.baseurl;
		}

		if ( type === "GET" ) {

			/**
			 * Format params to Querystring and concat to request url
			 */
			queryString = this.serialize( data );

			if ( queryString.length > 0 ) {
				return ( url.indexOf( '?' ) > -1 ) ? url + "&" + queryString : url + "?" + queryString;
			}
			else {
				return url;
			}
		}
		else {

			/**
			 * Use by default the given url
			 */
			return url;
		}
	}

	/**
	 * Create a FormData from object
	 * @param  {Object} data
	 * @return {FormData}
	 */
	objectToFormData( data, formData, prefix ) {

		if  ( formData == undefined ){
			formData = new FormData();
		}

		if ( data ) {

			for ( var key in data ) {

				let k = prefix ? prefix + "[" + key + "]" : key;

				if ( data[key]
						&& typeof data[key] == 'object'
						&& data[key] instanceof File == false
						&& data[key] instanceof Blob == false ){

					this.objectToFormData(data[key], formData, k);
				}
				else {
					if ( data[key] !== undefined && data[key] !== null ) {
						formData.append( k , data[key] );
					}
				}
			}
		}

		return formData;
	}

	/**
	 * Create a object from FormData
	 * @param  {FormData} data
	 * @return {Object}
	 */
	formDataToObject( data ) {

		let object = {};

		data.forEach(function(value, key){
			object[key] = value;
		});

		return object;
	}

	/**
	 * Format object to query string
	 * @param  {FormData} formData
	 * @return {String}
	 */
	serialize(object, prefix){

		let str = [], p;

		if ( object instanceof FormData ) {
			object = this.formDataToObject(object);
		}

		for (p in object) {

			if (object.hasOwnProperty(p)) {

				let k = prefix ? prefix + "[" + p + "]" : p,
				v = object[p];

				str.push((v !== null && typeof v === "object") ?
					this.serialize(v, k) :
					encodeURIComponent(k) + "=" + encodeURIComponent(v));
			}
		}

		return str.join("&");
	}

	toKeyValue ( object, list, key) {

		if ( object !== null && typeof object == 'object' ) {

			Object.keys(object).forEach( (k) => {

				if ( object[k] !== null ) {

					if ( typeof object[k] == 'object' ) {
						list = this.toKeyValue(object[k], list, key + '[' + k + ']');
					}
					else {
						if ( object[k] !== undefined && object[k] !== null ) {
							list[key + '[' + k + ']'] = object[k];
						}
					}
				}
			});
		}

		return list;
	}
}

export default ServerRequest;


/*
---
name: Server Request
category: JavaScript
---
XMLHttpRequest wrapper to send requests to the server. Response of the server will be automatically formatted based on the response headers.

Constructor
======
The options are based on the Nutshell.options.ServerRequest object.

##### Arguments:
- **Options** (Object) - List of options for sending data

##### Options
- **baseurl** (String) - Containing the URL to which the request is sent. Will be overridden if peanuts = false
- **secure** (String) - Encrypt the data before sending the package. Default: false
- **peanuts** (String) - Use peanuts setup.  Default: true
- **useMethod** (String) - Post to current page with method. Else the first param is of the call is a url. Default: true
- **headers** (Object) - List of default headers. Default: {}
- **status** (Boolean) - Status of the request is found in the header of the response. Otherwise the status is found in the body. Possible options: 'body' or 'header' Default: 'body'

```js
import { ServerRequest } from '@nutshell/core';

const rqst = new ServerRequest({
	secure: false,
	header: false,
	peanuts: true,
	baseurl: window.location.href
});
```

Or set the options globally

```js
import { Nutshell } from '@nutshell/core';

Nutshell.options.ServerRequest.baseurl = window.location.href;
Nutshell.options.ServerRequest.secure = false;
Nutshell.options.ServerRequest.peanuts = true;
Nutshell.options.ServerRequest.useMethod = false;
Nutshell.options.ServerRequest.status = 'header';
```

Functions
======

##### Arguments:
- **Target** (String) - Used as method if the option peanuts is true and useMethod is true. Otherwise target is used as url
- **Parameters** (Object) - Parameters will be posted as formdata or querystring based on request method
- **Options** (Object)
- **Files** (Object) - List with binary files.

##### Options
- **url** (String) - Override the current post url. By default request.baseurl or window.location.href will be used
- **encrypt** - (Boolean) - Encrypt your data before sending it to the server. Files are currently not supported


Perform a get request
```js
const rqst = new ServerRequest();
rqst.get('method');
```

Perform a post request
```js
import { ServerRequest} from '@nutshell/core';

const rqst = new ServerRequest();
rqst.post('method', { name, address });
```

Perform a submit on a form
```js
import { ServerRequest} from '@nutshell/core';

const rqst = new ServerRequest();
rqst.submit('#selector', 'method', {extra: 'test'});
```

Perform a read request. This is a GET request with a custom url. No parameters or options are required
```js
import { ServerRequest } from '@nutshell/core';

const rqst = new ServerRequest();
rqst.read(url);
```

Perform a request to a url

```js

//Globally override option `use method`
Nutshell.options.ServerRequest.useMethod = false;

import { ServerRequest} from '@nutshell/core';

const rqst = new ServerRequest();
rqst.post('/api/example', { name, address });

```

Vue
======
In a Nutshell App Setup this class is added to the components and store. The class is accessible with `this.$xhr` variable.

*/